import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _path from 'ramda/src/path';
import resx from '../../../utilities/resx';
import {
    RESX_DASHBOARD,
    RESX_REBLASTS,
    RESX_GLOBAL,
    LOGGLY_LEVEL_ERROR,
    LOGGLY_LEVEL_INFO,
    MIXPANEL_DELETE
} from '../../../Constants';
import Button from '../Button';
import ConfirmAlert from '../ConfirmAlert';
import Spinner from '../Spinner';
import reblast from '../../../API/Reblast';
import saveReblast from '../../../API/SaveReblast';
import removeReblast from '../../../API/RemoveReblast';
import { withMessage } from '../Message';
import loggly from '../../../API/Loggly';
import mixpanel from '../../../API/Mixpanel';

const ReblastActions = ({
    state,
    setState,
    isGroupAdmin,
    isFeed,
    deleteReblastFromState,
    message,
    closeDropdown,
    canPinPost
}) => {
    const isReblastOwner = state.IsReblastOwner;
    const reblastId = state.ReblastId;
    const userWebsiteId = state.User.WebsiteId;

    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);

    const isModerator = _path(['User2', 'LoggedInUserIsAdminOrStaff'], RESAAS);
    const isIdentifiedUser = _path(['User2', 'UserId'], RESAAS);
    let loadingTimer;

    const handleClickDropdown = () => {
        setShowDropdown(!showDropdown);
    };

    const handleDeleteCatchError = error => {
        setShowDropdown(false);
        clearTimeout(loadingTimer);
        setLoading(false);
        message.addMessage({ message: resx(RESX_GLOBAL, 'GENERIC_WEBSERVICE_ERROR_MESSAGE'), type: 'error' });
        loggly.add(LOGGLY_LEVEL_ERROR, `Reblast could not be deleted: ${error}`);
    };

    const inSessionStorage = () => {
        var rtValue = false;

        for (let i = 0; i < sessionStorage.length; i++) {
            const obj = sessionStorage.getItem(sessionStorage.key(i));
            if (obj === reblastId) {
                rtValue = true;
            }
        }

        return rtValue;
    };

    const addToSessionStorage = () => {
        if (window.Modernizr && window.Modernizr.sessionstorage) {
            let reblastIdx = Object.keys(window.sessionStorage).filter(key => key.includes('reblast#')).length;

            if (!inSessionStorage(reblastId)) {
                const index = `reblast#${++reblastIdx}`; // increment reblastIdx
                // save to sessionStorage
                sessionStorage.setItem(index, reblastId);
            }
        }
    };

    const handleDeleteClick = () => {
        ConfirmAlert({
            description: resx(RESX_DASHBOARD, 'DASHBOARD_REBLAST_DELETE_CONFIRM'),
            confirmLabel: resx(RESX_DASHBOARD, 'DASHBOARD_REBLAST_TEXT_DELETE'),
            abortLabel: resx(RESX_DASHBOARD, 'DASHBOARD_REBLAST_TEXT_CANCEL')
        }).then(() => {
            loadingTimer = setTimeout(() => {
                setLoading(true);
            }, 1000);
            if (isModerator) {
                removeReblast
                    .delete(reblastId, userWebsiteId)
                    .then(response => {
                        if (!_path(['data', 'RemoveReblastResult', 'Success'], response)) {
                            throw new Error(_path(['data', 'SaveReblastResult', 'Message'], response));
                        }

                        mixpanel.add(MIXPANEL_DELETE, { content: 'Reblast Text', type: 'Group Admin Delete Reblast' });

                        clearTimeout(loadingTimer);
                        setShowDropdown(false);
                        setLoading(false);
                        addToSessionStorage();

                        if (isFeed) {
                            deleteReblastFromState(reblastId);
                        } else {
                            window.location.assign('/');
                        }
                    })
                    .catch(error => {
                        handleDeleteCatchError(error);
                        loggly.add(LOGGLY_LEVEL_ERROR, `Failed to delete reblast from /removeReblast`, error);
                    });
            } else {
                saveReblast
                    .delete(reblastId)
                    .then(response => {
                        if (!_path(['data', 'SaveReblastResult', 'Success'], response)) {
                            throw new Error(_path(['data', 'SaveReblastResult', 'Message'], response));
                        }
                        mixpanel.add(MIXPANEL_DELETE, { content: 'Reblast Text' });

                        clearTimeout(loadingTimer);
                        setShowDropdown(false);
                        setLoading(false);
                        addToSessionStorage();

                        if (isFeed) {
                            deleteReblastFromState(reblastId);
                        } else {
                            window.location.assign('/');
                        }
                    })
                    .catch(error => {
                        handleDeleteCatchError(error);
                        loggly.add(LOGGLY_LEVEL_ERROR, `Failed to delete reblast from /saveReblast`, error);
                    });
            }
        });
    };

    const handlePinClick = () => {
        const isFeatured = !state.IsFeatured;

        setState(prevState => ({ ...prevState, IsFeatured: isFeatured }));

        reblast
            .updateReblastIsFeatured({
                IsFeatured: isFeatured,
                Identifier: reblastId,
                FirmId: state.FirmID
            })
            .then(() => {
                const m = isFeatured ? 'PINNED_POST_SUCCESS' : 'UNPINNED_POST_SUCCESS';
                message.addMessage({ message: resx(RESX_GLOBAL, m), type: 'success' });
            })
            .catch(error => {
                setState(prevState => ({ ...prevState, IsFeatured: !isFeatured }));
                message.addMessage({ message: resx(RESX_GLOBAL, 'GENERIC_WEBSERVICE_ERROR_MESSAGE'), type: 'error' });
                loggly.add(LOGGLY_LEVEL_ERROR, `pin-unpin error:  ${error}`);
            });
    };

    const handleReportClick = () => {
        loggly.add(LOGGLY_LEVEL_INFO, `Innappropriate Content Report: Reblast: ${reblastId}`);
        message.addMessage({ message: resx(RESX_GLOBAL, 'REPORT_INAPPROPRIATE_CONTENT_SUCCESS'), type: 'warning' });
    };

    useEffect(
        () => {
            setShowDropdown(false);
        },
        [closeDropdown]
    );

    return (
        <div className="re-reblast-dropdown-container re-reblast-details-actions" data-testid="ReblastActions">
            {isIdentifiedUser ? (
                <button
                    className="reblast-dropdown re-icon re-icon-xs re-icon-dropDown re-btn-cancel"
                    data-testid="DropdownBtn"
                    onClick={handleClickDropdown}
                    type="button"
                    onBlur={() => {
                        setTimeout(() => {
                            setShowDropdown(false);
                        }, 250);
                    }}
                />
            ) : null}

            {showDropdown ? (
                <ul className="reblast-menu-items" data-testid="postActions">
                    {canPinPost ? (
                        <li className="reblast-actions-button" data-testid="pin-button">
                            <Button
                                className="re-btn re-btn-cancel re-btn-small"
                                dataTestid="pinPostButton"
                                onClick={handlePinClick}
                                text={`${
                                    state.IsFeatured ? resx(RESX_GLOBAL, 'UNPIN_POST') : resx(RESX_GLOBAL, 'PIN_POST')
                                }`}
                                type="button"
                            />
                        </li>
                    ) : null}
                    {!isReblastOwner ? (
                        <li className="reblast-actions-button" data-testid="report-button">
                            <Button
                                className="re-btn re-btn-cancel re-btn-small"
                                dataTestid="ReportBtn"
                                onClick={handleReportClick}
                                text={resx(RESX_GLOBAL, 'REPORT_INAPPROPRIATE_CONTENT')}
                                type="button"
                            />
                        </li>
                    ) : null}
                    {isReblastOwner || isModerator || isGroupAdmin ? (
                        <li className="reblast-actions-button">
                            <Button
                                className="re-btn re-btn-cancel re-btn-small"
                                dataTestid="DeleteBtn"
                                loading={loading}
                                onClick={handleDeleteClick}
                                text={resx(RESX_REBLASTS, 'DELETE_REBLAST_TEXT')}
                                type="button"
                            />
                        </li>
                    ) : null}
                </ul>
            ) : null}
        </div>
    );
};

ReblastActions.propTypes = {
    state: PropTypes.object,
    setState: PropTypes.func,
    message: PropTypes.object,
    isFeed: PropTypes.bool,
    deleteReblastFromState: PropTypes.func,
    closeDropdown: PropTypes.number.isRequired,
    canPinPost: PropTypes.bool
};

export default withMessage(ReblastActions);
